import type { RoleCode } from '../../rbac/types';

export type TaskCaseDetailsReturnState = {
  returnTo: string;
  returnLabelKey: string;
};

export const TASK_CASE_RETURN_STATE_KEY = 'agd:taskCaseDetailsReturn';

export const TASKS_CASES_RETURN: TaskCaseDetailsReturnState = {
  returnTo: '/tasks',
  returnLabelKey: 'tasks.cases.breadcrumb',
};

export const ATTENDING_TASKS_RETURN: TaskCaseDetailsReturnState = {
  returnTo: '/tasks/attending',
  returnLabelKey: 'tasks.attending.breadcrumb',
};

export const NOTIFICATIONS_RETURN: TaskCaseDetailsReturnState = {
  returnTo: '/notifications',
  returnLabelKey: 'notifications.breadcrumb',
};

export function escalationTasksReturn(tab: 'assigned' | 'unassigned'): TaskCaseDetailsReturnState {
  return {
    returnTo: `/tasks/escalation?tab=${tab}`,
    returnLabelKey:
      tab === 'assigned' ? 'tasks.escalation.breadcrumbAssigned' : 'tasks.escalation.breadcrumbUnassigned',
  };
}

export function assignTasksReturn(tab: 'unassigned' | 'assigned' | 'reallocation'): TaskCaseDetailsReturnState {
  const returnLabelKey =
    tab === 'assigned'
      ? 'tasks.assign.breadcrumbAssigned'
      : tab === 'reallocation'
        ? 'tasks.assign.breadcrumbReallocation'
        : 'tasks.assign.breadcrumbUnassigned';

  return {
    returnTo: `/tasks/assign?tab=${tab}`,
    returnLabelKey,
  };
}

export function dashboardReturn(variant: 'sg' | 'operational'): TaskCaseDetailsReturnState {
  const prefix = variant === 'sg' ? 'sgDashboard' : 'operationalDashboard';
  return {
    returnTo: `/dashboards/${variant}`,
    returnLabelKey: `${prefix}.breadcrumb`,
  };
}

export function isTaskCaseDetailsReturnState(value: unknown): value is TaskCaseDetailsReturnState {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<TaskCaseDetailsReturnState>;
  return (
    typeof candidate.returnTo === 'string' &&
    candidate.returnTo.startsWith('/') &&
    typeof candidate.returnLabelKey === 'string' &&
    candidate.returnLabelKey.length > 0
  );
}

export function getTaskCaseDetailsReturnState(state: unknown): TaskCaseDetailsReturnState | undefined {
  if (typeof state !== 'object' || state === null || !('taskCaseReturn' in state)) {
    return undefined;
  }
  const taskCaseReturn = (state as { taskCaseReturn?: unknown }).taskCaseReturn;
  return isTaskCaseDetailsReturnState(taskCaseReturn) ? taskCaseReturn : undefined;
}

export function getTaskCaseDetailsReturnFromSearchParams(
  searchParams: URLSearchParams,
): TaskCaseDetailsReturnState | undefined {
  const returnTo = searchParams.get('returnTo')?.trim();
  const returnLabelKey = searchParams.get('returnLabelKey')?.trim();
  if (!returnTo || !returnLabelKey) return undefined;
  return isTaskCaseDetailsReturnState({ returnTo, returnLabelKey }) ? { returnTo, returnLabelKey } : undefined;
}

export function appendReturnStateToTaskCasePath(
  path: string,
  returnState: TaskCaseDetailsReturnState,
): string {
  const url = new URL(path, 'http://local');
  url.searchParams.set('returnTo', returnState.returnTo);
  url.searchParams.set('returnLabelKey', returnState.returnLabelKey);
  return `${url.pathname}${url.search}`;
}

export function persistTaskCaseDetailsReturnState(state: TaskCaseDetailsReturnState): void {
  sessionStorage.setItem(TASK_CASE_RETURN_STATE_KEY, JSON.stringify(state));
}

export function consumeTaskCaseDetailsReturnState(): TaskCaseDetailsReturnState | undefined {
  const raw = sessionStorage.getItem(TASK_CASE_RETURN_STATE_KEY);
  sessionStorage.removeItem(TASK_CASE_RETURN_STATE_KEY);
  if (!raw) return undefined;

  try {
    const parsed: unknown = JSON.parse(raw);
    return isTaskCaseDetailsReturnState(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export function resolveDefaultTaskCaseDetailsReturn(roleCode: RoleCode): TaskCaseDetailsReturnState {
  if (roleCode === 'STATE_ATTORNEY') return ATTENDING_TASKS_RETURN;
  if (roleCode === 'ALLOCATION_OFFICER') {
    return {
      returnTo: '/tasks/assign',
      returnLabelKey: 'tasks.assign.breadcrumb',
    };
  }
  // Management Assistant has no access to the shared /tasks screen — it uses its own
  // dedicated case-details list instead.
  if (roleCode === 'OFFICE_CLERK') {
    return {
      returnTo: '/tasks/case-details',
      returnLabelKey: 'tasks.managementAssistant.breadcrumb',
    };
  }
  return TASKS_CASES_RETURN;
}

export function openTaskCaseDetailsInNewTab(path: string, returnState: TaskCaseDetailsReturnState): void {
  const url = appendReturnStateToTaskCasePath(path, returnState);
  window.open(`${window.location.origin}${url}`, '_blank', 'noopener,noreferrer');
}
